/**
 * ONH / ONL: the overnight (Globex) high and low.
 *
 * The overnight session for a trading day runs from 17:00 CT the calendar day
 * before (the CME roll) to the 08:30 CT cash open. Its high and low are the
 * first levels the cash session usually tests.
 *
 * - Before 08:30 CT the overnight is still running: the values are DEVELOPING
 *   (drawn lighter) and move with every new extreme.
 * - From 08:30 CT they are FIXED for the rest of that trading day.
 * - At the 17:00 CT roll the next trading day begins and a new developing
 *   overnight starts from its first bar.
 *
 * "Now" is the start of the latest loaded bar, not the wall clock, so the
 * result is a pure function of the data (a stalled feed shows what the data
 * says, and tests are deterministic).
 *
 * Source data: the store's raw candles (base interval, 2-min in prod) - the
 * same bars MOC uses, independent of the chart's timeframe. A bar belongs to
 * the overnight when it STARTS in [17:00 CT prev day, 08:30 CT); with 1/2/5
 * minute bars 08:30 is a bar boundary, so no cash-session print leaks in.
 *
 * Wall-clock maths goes through Intl with the real America/Chicago zone, so it
 * is DST-safe: 08:30 CT is 13:30 UTC in summer and 14:30 UTC in winter.
 */

import type { Candle } from '@shared/types';
import { chicagoWallTimeToMs, tradingDayOf, SESSION_ROLL_HOUR_CT } from './mocLevel';

/** Cash open, 08:30 CT: the end of the overnight. */
export const ON_END_HOUR_CT = 8;
export const ON_END_MINUTE_CT = 30;

export interface OnhOnl {
  high: number;
  low: number;
  /** True until 08:30 CT: the overnight is still running and the values can move. */
  developing: boolean;
  /** Trading day the overnight belongs to, `YYYY-MM-DD` (CT). */
  tradingDay: string;
  /** Overnight window [start, end) in epoch ms. */
  windowStart: number;
  windowEnd: number;
}

/** The overnight window [17:00 CT prev day, 08:30 CT) of the trading day containing `ms`. */
export function overnightWindow(ms: number): { start: number; end: number; tradingDay: string } {
  const td = tradingDayOf(ms);
  const prev = new Date(Date.UTC(td.year, td.month - 1, td.day - 1));
  const start = chicagoWallTimeToMs(
    prev.getUTCFullYear(), prev.getUTCMonth() + 1, prev.getUTCDate(), SESSION_ROLL_HOUR_CT, 0, 0,
  );
  const end = chicagoWallTimeToMs(td.year, td.month, td.day, ON_END_HOUR_CT, ON_END_MINUTE_CT, 0);
  return { start, end, tradingDay: td.key };
}

/**
 * ONH/ONL for the trading day of the latest loaded bar, or null when that
 * trading day has no overnight bars (a holiday, or data that starts mid-day).
 *
 * `candles` must be ascending by timestamp (the store's invariant). Scans
 * backwards from the end and stops at the window start, so the cost is one
 * overnight's worth of bars.
 */
export function computeOnhOnl(candles: readonly Candle[] | null | undefined): OnhOnl | null {
  if (!candles || candles.length === 0) return null;
  let last = candles.length - 1;
  while (last >= 0 && !Number.isFinite(candles[last].timestamp)) last--;
  if (last < 0) return null;

  const nowTs = candles[last].timestamp;
  const { start, end, tradingDay } = overnightWindow(nowTs);

  let high = -Infinity;
  let low = Infinity;
  for (let i = last; i >= 0; i--) {
    const c = candles[i];
    if (!Number.isFinite(c.timestamp)) continue;
    if (c.timestamp < start) break;
    if (c.timestamp >= end) continue;
    if (c.high > 0 && c.high > high) high = c.high;
    if (c.low > 0 && c.low < low) low = c.low;
  }
  if (!Number.isFinite(high) || !Number.isFinite(low)) return null;

  return { high, low, developing: nowTs < end, tradingDay, windowStart: start, windowEnd: end };
}
